import React, { useMemo } from 'react'
import { makeStyles, useTheme } from '@mui/styles'

import lang from '@/lang'
import { Signer } from '@/modules/signature/types'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconAdd from '@assets/images/icons/plus.svg'
import IconTrash from '@assets/images/icons/trashcan.svg'
import IconInfo from '@assets/images/icons/info.svg'
import { SquareIconButton } from '@/modules/base/components/buttons'
import ThemeInterface from '@/theme-interface'
import MultiSelect from '@/components/multi-select/multi-select'
import { signatureWizardScrollElId } from '@/modules/signature/components/wizard/signature-wizard'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { useAppSelector } from '@/redux/store'
import { MultiSelectItemObj } from '@/components/multi-select/types'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  selectionItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  seperator: {
    borderBottom: `1px solid ${theme.borderColors.default}`,
    margin: '20px 0px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: theme.fontSizes.smedium,
    paddingBottom: 20,
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  headerInfoIcon: {
    marginLeft: 10,
    marginBottom: 2,
  },
  tooltipHeader: {
    fontWeight: 500,
    fontSize: theme.fontSizes.small,
  },
  tooltipInfo: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
  },
  addWrapper: {
    display: 'inline-block',
    color: theme.primary,
    marginTop: 10,
    cursor: 'pointer',
  },
  addLabel: {
    marginLeft: 10,
  },
}), { name: 'SignerSelection' })

type SignerSelectionProps = {
  signers: Signer[],
  tenantCollaborationId?: string,
  onAdd?: () => void,
  onDelete?: (index: number) => void,
  onChange?: (index: number, signer: Signer | null) => void
}

const SignerSelection = (props: SignerSelectionProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const {
    tenantCollaborationId = null,
    signers = [],
    onAdd,
    onDelete,
    onChange,
  } = props
  const subscriptions = useAppSelector(state => state.tenant.info.subscriptions)
  const isTaxConsultant = useMemo(() => { return subscriptions?.some(s => s.isTaxConsultant) }, [subscriptions])

  const handleDeleteSigner = (index: number): void => {
    onDelete?.(index)
  }
  const handleSignerChange = (index, signer): void => {
    onChange?.(index, signer)
  }
  const handleAddClick = (): void => {
    onAdd?.()
  }

  const infoIcon = isTaxConsultant && (
    <RichTooltip
      content={
        (
          <>
            <div className={classes.tooltipHeader}>{lang.signature.form.signer.info.heading}</div>
            <div className={classes.tooltipInfo}>
              {lang.signature.form.signer.info.description}
            </div>
          </>
        )
      }
      placement='bottom'>
      <SVGIcon
        className={classes.headerInfoIcon}
        svg={IconInfo}
        height="13px"
        width="13px" />
    </RichTooltip>
  )

  const signerElements = useMemo(() => {
    return signers.map((signer, index) => {
      const label = `${lang.signature.form.signer.label} ${index + 1}`

      return (
        <div key={`signer-${index}`} className={classes.selectionItem}>
          <div className={classes.header}>
            <div className={classes.headerLabel}>
              <span>{label}</span>
              {infoIcon}
            </div>
            {signers.length > 1 && <SquareIconButton
              icon={IconTrash}
              size='small'
              onClick={() => handleDeleteSigner(index)} />
            }
          </div>
          <MultiSelect
            dataQsId='signerSelection-multiSelect'
            value={signer as MultiSelectItemObj}
            hiddenValues={signers as MultiSelectItemObj[]}
            valueKey='email'
            tenantCollaborationId={tenantCollaborationId}
            popperScrollElId={signatureWizardScrollElId}
            onChange={(user) => handleSignerChange(index, user)} />
          {signers.length > 1 && <div className={classes.seperator} />}
        </div>
      )
    })
  }, [signers])

  return (
    <div>
      {signerElements}
      <div className={classes.addWrapper} onClick={handleAddClick}>
        <SVGIcon svg={IconAdd} height="10px" width="10px" fill={theme.primary} />
        <span className={classes.addLabel}>{lang.signature.form.signer.add}</span>
      </div>
    </div>
  )
}

export default SignerSelection
